import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, ExternalLink, Loader2, Smartphone, Target, Upload } from 'lucide-react';
import { defaultSnapConversion, snapGoalFor } from './snapchatConfig';

const inputCls = 'bg-white/5 border-white/10 text-white placeholder:text-white/30';

const AssetSelect = ({ label, items, value, onChange, emptyHint, manageUrl, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-white/80 text-sm">{label} *</Label>
    {items.length > 0 ? (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className={inputCls} data-testid={testId}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent className="bg-slate-800 border-white/10">
          {items.map((i) => (
            <SelectItem key={i.id} value={i.id} className="text-white hover:bg-white/10">{i.name || i.id}{i.status && i.status !== 'ACTIVE' ? ` (${i.status.toLowerCase()})` : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200" data-testid={`${testId}-empty`}>
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>{emptyHint} <a href={manageUrl} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Open Ads Manager <ExternalLink className="h-3 w-3" /></a>, then click Refresh.</span>
      </div>
    )}
  </div>
);

export const SnapchatObjectiveSetup = ({ snap, formData, value, onChange }) => {
  const c = { ...defaultSnapConversion(), ...(value || {}) };
  const set = (patch) => onChange({ ...c, ...patch });
  const { conversionAssets: assets, fetchConversionAssets, selectedAccount, connection, uploadMedia } = snap;
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconRef = useRef(null);
  const goal = snapGoalFor(formData.objective, formData.snapOptimizationGoal);
  const needs = goal?.needs;

  useEffect(() => {
    if (needs && connection?.connected && selectedAccount?.id && !assets) fetchConversionAssets(selectedAccount.id);
  }, [needs, connection?.connected, selectedAccount?.id, assets, fetchConversionAssets]);

  if (!needs) return null;
  if (!connection?.connected) {
    return <p className="text-xs text-amber-300" data-testid="snapchat-objective-setup-locked">Connect Snapchat to load your pixels, lead forms and apps for this objective.</p>;
  }
  const acct = selectedAccount?.id;
  const manager = (path) => `https://ads.snapchat.com/${acct}/${path}`;

  const handleIcon = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const media = await uploadMedia(file, acct, 'icon');
      set({ iconMediaId: media.media_id, iconFileName: file.name });
      toast.success('App icon uploaded to Snapchat');
    } catch (error) {
      toast.error('Icon upload failed', { description: error.message });
    } finally {
      setUploadingIcon(false);
    }
  };

  return (
    <div className="space-y-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="snapchat-objective-setup">
      <div className="flex items-center justify-between">
        <Label className="text-white/90 text-sm font-medium flex items-center gap-2"><Target className="h-4 w-4" /> Snapchat setup for {goal.label}</Label>
        <button type="button" onClick={() => fetchConversionAssets(acct)} className="text-xs text-yellow-200 hover:text-white" data-testid="snapchat-assets-refresh-btn">Refresh</button>
      </div>
      {!assets && <p className="text-xs text-white/50 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading your Snapchat assets…</p>}

      {needs === 'pixel' && assets && (
        <>
          <AssetSelect label="Snap Pixel" items={assets.pixels} value={c.pixelId} onChange={(v) => set({ pixelId: v })} testId="snapchat-pixel-select"
            emptyHint="No Snap Pixel found in this ad account. Create one under Events Manager" manageUrl={manager('events-manager')} />
          <p className="text-xs text-white/40">Make sure the pixel is installed on your Destination URL (Creative tab) so Snapchat can track {goal.label.toLowerCase()}.</p>
        </>
      )}

      {needs === 'lead_form' && assets && (
        <AssetSelect label="Lead form" items={assets.lead_forms} value={c.leadFormId} onChange={(v) => set({ leadFormId: v })} testId="snapchat-lead-form-select"
          emptyHint="No lead generation form found. Create one under Lead Generation Forms" manageUrl={manager('lead-gen-forms')} />
      )}

      {needs === 'app' && assets && (
        <div className="space-y-3">
          <AssetSelect label="Snap App ID" items={assets.mobile_apps} value={c.snapAppId} testId="snapchat-app-select"
            onChange={(v) => {
              const app = assets.mobile_apps.find((a) => a.id === v);
              set({ snapAppId: v, appName: c.appName || app?.name || '', iosAppId: c.iosAppId || app?.ios_app_id || '', androidAppUrl: c.androidAppUrl || app?.android_app_url || '' });
            }}
            emptyHint="No app registered in this ad account. Register your app under Apps" manageUrl={manager('apps')} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-white/80 text-sm">App name *</Label>
              <Input value={c.appName} maxLength={50} onChange={(e) => set({ appName: e.target.value })} placeholder="Adlinka" className={inputCls} data-testid="snapchat-app-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80 text-sm">iOS App ID</Label>
              <Input value={c.iosAppId} onChange={(e) => set({ iosAppId: e.target.value.replace(/\D/g, '') })} placeholder="e.g. 447188370" className={inputCls} data-testid="snapchat-ios-app-id-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-white/80 text-sm">Android package name</Label>
              <Input value={c.androidAppUrl} onChange={(e) => set({ androidAppUrl: e.target.value.trim() })} placeholder="e.g. com.adlinka.app" className={inputCls} data-testid="snapchat-android-package-input" />
              <p className="text-xs text-white/40">At least one of iOS App ID or Android package is required.</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/80 text-sm flex items-center gap-2"><Smartphone className="h-4 w-4" /> App icon * (square PNG, ≥200×200)</Label>
            <input ref={iconRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => handleIcon(e.target.files?.[0])} data-testid="snapchat-app-icon-input" />
            <button type="button" onClick={() => iconRef.current?.click()} disabled={uploadingIcon} className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/20 text-sm text-white/70 hover:bg-white/5" data-testid="snapchat-app-icon-btn">
              {uploadingIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : c.iconMediaId ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Upload className="h-4 w-4" />}
              {uploadingIcon ? 'Uploading icon…' : c.iconMediaId ? `Icon uploaded (${c.iconFileName || c.iconMediaId}) — click to replace` : 'Upload app icon'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
